import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Input, useViewport, Card, Toast, Typography, useInput } from '@chilecompra/react-kit/components'
import {
  checkInputErrorEmail,
  checkInputErrorMaxLength,
  checkInputErrorRequired,
  EMAIL_CHECK
} from '@chilecompra/react-kit/utils'
import { useEffectOnce } from '@chilecompra/react-kit'
import { REACT_APP_TRAINING_URL } from '../../../config/settings/environment'
import { onDownloadPDFThunk, onSendPdfEmailThunk, registerFlowThunk } from '../RegisterWizard.actions'
import {
  Root,
  Divider,
  Grid,
  Section,
  Title,
  Image,
  Strong,
  Link,
  ToastContainer,
  SectionInputContainer,
  ImageContainer,
  ReceiptContainer,
  ReceiptNumber,
  DividerWrapper,
  StyledHR,
  DividerText,
  StrongToast
} from './RegisterWizardComplete.styles'

/**
 * The RegisterWizardComplete' container.
 */
const RegisterWizardComplete = () => {
  const { error, loading } = useSelector(state => state.registerWizard.finishInscription)
  const { emailSent, sendingEmail, errorSendingEmail } = useSelector(state => state.registerWizard.pdfEmailState)
  const inscriptionId = useSelector(state => state.registerWizard.inscriptionId)
  const { size } = useViewport()
  const dispatch = useDispatch()
  let token = useSelector(state => state.recaptcha.token)
  const flow = 'INICIO_PASO_6'

  if (!token) {
    token = '1234'
  }

  const handleDownloadReceipt = () => {
    dispatch(onDownloadPDFThunk({ inscriptionId }))
  }

  useEffectOnce(() => {
    dispatch(registerFlowThunk({ flow }))
  }, [dispatch])

  const requestNumber = (
    <>
      <Strong>{inscriptionId}</Strong>
    </>
  )

  const renderToast = () => {
    return (
      <ToastContainer role="alert" aria-live="assertive">
        <Toast
          tabIndex="0"
          disableIcon
          margin={size.isTiny ? '22px 0 13px 0' : size.isDownToLarge ? '18px 0 0' : '53px 0 0'}
          severity="default"
          width={size.isTiny ? '90%' : size.isDownToLarge ? '100%' : '263px'}
        >
          <StrongToast>Aprende más sobre la plataforma de Mercado Público</StrongToast>
          <Divider />
          ChileCompra ha preparado una capacitación inicial para que conozcas la plataforma de Mercado Público, sus
          distintos módulos y explorar sus funcionalidades.
          <Divider />
          <Link
            tabIndex="0"
            aria-label="Ir a capacitación"
            color="primary"
            underline
            href={REACT_APP_TRAINING_URL}
            target="_blank"
            rel="noreferrer"
          >
            Ir a capacitación
          </Link>
        </Toast>
      </ToastContainer>
    )
  }

  const {
    error: emailError,
    value: emailValue,
    onChange: handleEmailChange
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired('Debes ingresar tu dirección de correo.'),
      checkInputErrorEmail('Tu dirección de correo parece estar en un formato incorrecto.'),
      checkInputErrorMaxLength(80, 'Tu dirección de correo excede el máximo de 80 carácteres permitidos.')
    ],
    initialValue: ''
  })

  const handleSendingReceipt = () => {
    const requestCode = inscriptionId
    const data = {
      email: emailValue,
      requestCode
    }
    dispatch(onSendPdfEmailThunk(data))
  }

  const canNotSendingReceipt = useCallback(() => {
    const hadErrors = emailError !== ''
    const hadInvalidValues = emailValue === ''
    const hadErrorEmail = !EMAIL_CHECK.test(emailValue)

    return hadErrors || hadInvalidValues || hadErrorEmail || sendingEmail
  }, [emailError, emailValue, sendingEmail])

  const disabledSendingReceipt = canNotSendingReceipt()

  return (
    <Root>
      <Grid container spacing={size.isTiny ? 0 : 2}>
        <Grid item xs={12} md={12} lg={7} xl={7}>
          <Title tabIndex="0" aria-label="Paso 6: ¡Solicitud de inscripción enviada!">
            ¡Solicitud de inscripción enviada!
          </Title>
          {!error && (
            <Section>
              <ImageContainer>
                <Image src="/assets/img/steps/icono_send_sol.svg" alt="¡Solicitud de inscripción enviada!" />
              </ImageContainer>
              <Card enableBorder color="blank" margin="0 0 30px">
                <ReceiptContainer>
                  <Typography tabIndex="0" variant="body1" margin="0 0 20px" textAlign="center">
                    Tu solicitud de inscripción fue generada <br /> con el siguiente número:
                  </Typography>
                  <ReceiptNumber tabIndex="0">{requestNumber}</ReceiptNumber>
                </ReceiptContainer>
              </Card>
              <Grid container item alignItems="left">
                <Typography tabIndex="0" variant="body1" margin="0 0 30px" textAlign="left">
                  Con este número, podrás llamar a la mesa de ayuda y consultar por la solicitud de tu inscripción.
                </Typography>
                <Typography tabIndex="0" variant="body1" margin="0 0 30px" textAlign="left">
                  La solicitud será revisada por el departamento de Gestión de Usuarios y tendrás tu respuesta en un
                  plazo máximo de <Strong>20 días hábiles.</Strong>
                </Typography>
                {error && !loading && errorSendingEmail && (
                  <Toast tabIndex="0" margin="20px 0" severity="warning" width="90%">
                    En estos momentos no podemos enviarte el comprobante, por favor espera unos minutos e intenta de
                    nuevo.
                  </Toast>
                )}
                <Typography tabIndex="0" variant="body1" fontSize="14px" margin="0 0 10px">
                  Puedes enviar el comprobante a un correo electrónico:
                </Typography>
                <SectionInputContainer
                  width="100%"
                  display="flex"
                  flexDirection={size.isTiny || size.isDownToLarge ? 'column' : 'row'}
                  alignItems="center"
                  gap="30px"
                  margin="10px 0 0 0"
                >
                  <Input
                    autoFocus
                    error={emailError}
                    label="Correo electrónico"
                    placeholder="email@ejemplo.com"
                    aria-label="Correo electrónico"
                    value={emailValue}
                    onChange={handleEmailChange}
                  />
                  <Button
                    tabIndex="0"
                    aria-label={!emailSent ? 'Enviar' : '¡Enviado!'}
                    color="default"
                    disabled={disabledSendingReceipt}
                    loading={sendingEmail}
                    variant={!emailSent ? 'contained' : 'outlined'}
                    onClick={handleSendingReceipt}
                    width={size.isTiny ? '90%' : size.isDownToLarge ? '100%' : '42%'}
                    margin={size.isTiny ? '-25px 0 10px 0' : size.isDownToLarge ? '-30px 0 0' : ''}
                  >
                    {!emailSent ? 'Enviar' : '¡Enviado!'}
                  </Button>
                </SectionInputContainer>
                {emailSent && (
                  <Toast tabIndex="0" margin="15px 0 0 0" width={size.isTiny ? '90%' : '100%'} severity="success">
                    Hemos enviado el comprobante de solicitud de inscripción al correo señalado.
                  </Toast>
                )}
              </Grid>
              <Typography variant="body1" margin="30px 0 30px" component="div">
                <DividerWrapper>
                  <StyledHR />
                  <DividerText>o bien</DividerText>
                  <StyledHR />
                </DividerWrapper>
              </Typography>
              <ImageContainer>
                <Button
                  tabIndex="0"
                  aria-label="Descargar comprobante como PDF"
                  color="primary"
                  variant="outlined"
                  onClick={handleDownloadReceipt}
                  width={size.isTiny ? '90%' : size.isDownToLarge ? '100%' : '80%'}
                  margin={size.isTiny ? '0 0 10px 0' : size.isDownToLarge ? '-15px 0 0' : ''}
                >
                  Descargar comprobante como PDF
                </Button>
              </ImageContainer>
            </Section>
          )}
          {error && error?.reason === 'SOMETHING_WENT_WRONG_ERROR' && (
            <Toast
              tabIndex="0"
              margin={size.isTiny ? '22px 0 13px 0' : '34px 0 0'}
              padding="8px 16px"
              severity="warning"
            >
              Error en servicio externo, puedes intentar nuevamente en unos minutos para continuar con tu inscripción.
            </Toast>
          )}
        </Grid>
        {size.isDownToLarge && renderToast()}
        {size.isUpToLarge && (
          <Grid item lg={5} xl={5} margin="-25px 0 0 0">
            {size.isUpToLarge && renderToast()}
          </Grid>
        )}
      </Grid>
    </Root>
  )
}

export default RegisterWizardComplete
